import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../../../../api/apiClient";
import Loading from "../../../../components/Loading/Loading";
import "./CRMDashboard.css";

// ── Inline area chart ─────────────────────────────────────────────────────────
const AreaChart = ({ data = [], color = "#3b82f6" }) => {
    const W = 600, H = 148;
    const PAD = { top: 18, right: 12, bottom: 30, left: 36 };
    const iW = W - PAD.left - PAD.right;
    const iH = H - PAD.top  - PAD.bottom;

    const pts = useMemo(() => {
        if (!data.length) return null;
        const maxVal = Math.max(...data.map(d => d.count), 1);
        const n = data.length;
        const xs = data.map((_, i) => PAD.left + (i / Math.max(n - 1, 1)) * iW);
        const ys = data.map(d  => PAD.top  + iH - (d.count / maxVal) * iH);
        const line = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
        const fill = line
            + ` L${xs[n - 1].toFixed(1)},${(PAD.top + iH).toFixed(1)}`
            + ` L${xs[0].toFixed(1)},${(PAD.top + iH).toFixed(1)} Z`;
        return {
            line, fill,
            dots:   xs.map((x, i) => ({ x, y: ys[i], count: data[i].count })),
            labels: xs.map((x, i) => ({ x, label: data[i].month?.slice(5) || "" })),
            ticks:  [0, 0.5, 1].map(f => ({ y: PAD.top + iH - f * iH, val: Math.round(f * maxVal) })),
        };
    }, [data]);

    if (!pts) return <p className="odb-empty-small">No data yet.</p>;
    
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="odb-area-svg" preserveAspectRatio="xMidYMid meet">
            <defs>
                <linearGradient id="odb-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={color} stopOpacity="0.22" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>

            {pts.ticks.map(t => (
                <g key={t.y}>
                    <line x1={PAD.left} x2={PAD.left + iW} y1={t.y} y2={t.y}
                        stroke="var(--color-border)" strokeWidth="1" />
                    <text x={PAD.left - 6} y={t.y + 3.5} textAnchor="end"
                        fontSize="9" fill="var(--color-text-secondary)" fontFamily="inherit">
                        {t.val}
                    </text>
                </g>
            ))}

            <path d={pts.fill} fill="url(#odb-grad)" />
            <path d={pts.line} fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            {pts.dots.map((d, i) => (
                <circle key={i} cx={d.x} cy={d.y} r="3.5"
                    fill={color} stroke="var(--color-bg-primary)" strokeWidth="2" />
            ))}
            {pts.labels.map((l, i) => (
                <text key={i} x={l.x} y={H - 5} textAnchor="middle"
                    fontSize="10" fill="var(--color-text-secondary)" fontFamily="inherit">
                    {l.label}
                </text>
            ))}
        </svg>
    );
};

// ── Source palette ────────────────────────────────────────────────────────────
const SOURCE_COLORS = {
    website:       "#10b981",
    referral:      "#3b82f6",
    linkedin:      "#0ea5e9",
    trade_show:    "#8b5cf6",
    cold_outreach: "#f59e0b",
    partner:       "#ec4899",
    other:         "#94a3b8",
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const CRMDashboard = () => {
    const [stats, setStats]     = useState(null);
    const [pipeline, setPipeline] = useState(null);
    const [loading, setLoading]  = useState(true);

    useEffect(() => {
        Promise.all([
            apiClient.get("/admin/crm/stats"),
            apiClient.get("/admin/crm/reports/pipeline"),
        ]).then(([s, p]) => {
            setStats(s.data);
            setPipeline(p.data);
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;
    if (!stats)  return <p className="crm-empty">Failed to load stats.</p>;

    const maxStage  = Math.max(...(stats.stageDistribution?.map(s => s.leadCount) || [1]), 1);
    const maxSource = Math.max(...(pipeline?.bySource?.map(s => s.count) || [1]), 1);
    const hasSource = pipeline?.bySource?.length > 0;
    const pipelineValueFmt = stats.pipelineValue > 0
        ? `$${Number(stats.pipelineValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
        : "$0";

    const today = new Date().toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
    });

    return (
        <div className="odb-wrap">

            {/* ── Page header ───────────────────────────────────────── */}
            <div className="odb-header">
                <div className="odb-header__left">
                    <h2 className="odb-header__title">Overview</h2>
                    <span className="odb-header__date">{today}</span>
                </div>
            </div>

            {/* ── Hero cards ────────────────────────────────────────── */}
            <div className="odb-hero-row">
                <div className="odb-hero-card">
                    <span className="odb-hero-card__num">
                        {Number(stats.totalLeads).toLocaleString()}
                    </span>
                    <span className="odb-hero-card__label">Total Leads</span>
                </div>
                <div className="odb-hero-card">
                    <span className="odb-hero-card__num odb-hero-card__num--accent">
                        {Number(stats.newThisMonth).toLocaleString()}
                    </span>
                    <span className="odb-hero-card__label">New This Month</span>
                </div>
            </div>

            {/* ── Secondary metrics strip ───────────────────────────── */}
            <div className="odb-strip">
                <div className="odb-strip__metric">
                    <span className="odb-strip__val">{stats.conversionRate}%</span>
                    <span className="odb-strip__key">Conversion Rate</span>
                </div>
                <div className="odb-strip__divider" />
                <div className="odb-strip__metric">
                    <span className="odb-strip__val">{pipelineValueFmt}</span>
                    <span className="odb-strip__key">Pipeline Value</span>
                </div>
            </div>

            {/* ── Charts ────────────────────────────────────────────── */}
            <div className="odb-charts">

                {/* Area chart — leads over time */}
                <div className="odb-chart-card odb-chart-card--wide">
                    <div className="odb-chart-card__header">
                        <div>
                            <p className="odb-chart-card__title">Leads Over Time</p>
                            <p className="odb-chart-card__sub">Last 6 months</p>
                        </div>
                    </div>
                    <div className="odb-chart-card__body">
                        <AreaChart data={pipeline?.leadsOverTime || []} color="#3b82f6" />
                    </div>
                </div>

                {/* Stage distribution */}
                <div className="odb-chart-card">
                    <div className="odb-chart-card__header">
                        <div>
                            <p className="odb-chart-card__title">By Stage</p>
                            <p className="odb-chart-card__sub">Current distribution</p>
                        </div>
                    </div>
                    <div className="odb-chart-card__body odb-stage-list">
                        {!(stats.stageDistribution?.length) && (
                            <p className="odb-empty-small">No stage data yet.</p>
                        )}
                        {(stats.stageDistribution || []).map(s => {
                            const pct   = Math.round((s.leadCount / maxStage) * 100);
                            const color = s.color || "#6b7280";
                            return (
                                <div key={s.id} className="odb-stage-row">
                                    <div className="odb-stage-row__top">
                                        <span className="odb-stage-dot" style={{ background: color }} />
                                        <span className="odb-stage-name">{s.name}</span>
                                        <span className="odb-stage-count">{s.leadCount}</span>
                                    </div>
                                    <div className="odb-stage-track">
                                        <div className="odb-stage-fill"
                                            style={{ width: `${pct}%`, background: color }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>

            {/* ── Source breakdown (conditional) ────────────────────── */}
            {hasSource && (
                <div className="odb-chart-card odb-source-card">
                    <div className="odb-chart-card__header">
                        <div>
                            <p className="odb-chart-card__title">By Source</p>
                            <p className="odb-chart-card__sub">All time</p>
                        </div>
                    </div>
                    <div className="odb-source-grid">
                        {pipeline.bySource.map(s => {
                            const pct   = Math.round((s.count / maxSource) * 100);
                            const color = SOURCE_COLORS[s.source] || "#94a3b8";
                            return (
                                <div key={s.source} className="odb-source-row">
                                    <div className="odb-source-row__top">
                                        <span className="odb-source-dot" style={{ background: color }} />
                                        <span className="odb-source-name">
                                            {(s.source || "unknown").replace(/_/g, " ")}
                                        </span>
                                        <span className="odb-source-count">{s.count}</span>
                                    </div>
                                    <div className="odb-source-track">
                                        <div className="odb-source-fill"
                                            style={{ width: `${pct}%`, background: color }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

        </div>
    );
};

export default CRMDashboard;
